import { Callout, DataTable, Disclosure } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { IdeaLabEntryCoverageSummary } from '@einhaus/project314-types';
import type { IdeaLabRunDetail } from '../ideaLabUtils';
import { coverageReasonLabel, describeEntryCoverage } from '../coverageView';

type EntityCoverage = IdeaLabEntryCoverageSummary['byEntity'][number];
type MonthCoverage = IdeaLabEntryCoverageSummary['byMonth'][number];
const entityColumns: ColumnDef<EntityCoverage>[] = [
    { accessorKey: 'symbol', header: 'Symbol' },
    { accessorKey: 'entityType', header: 'Market' },
    { id: 'reason', header: 'Reason', cell: ({ row }) => coverageReasonLabel(row.original.reason) },
    { accessorKey: 'skippedSessionCount', header: 'Excluded sessions' },
    { accessorKey: 'firstSessionDate', header: 'First affected session' },
    { accessorKey: 'lastSessionDate', header: 'Last affected session' }
];
const monthColumns: ColumnDef<MonthCoverage>[] = [
    { accessorKey: 'month', header: 'Month (Eastern)' },
    { accessorKey: 'skippedSessionCount', header: 'Excluded symbol-sessions' }
];

const RunCoverageSummary = ({ coverage, run }: { coverage: IdeaLabEntryCoverageSummary; run: IdeaLabRunDetail }) => {
    const hasExclusions = coverage.skippedSessionCount > 0 || coverage.population.excludedEntityCount > 0;
    const excludedSymbols = run.excludedWorkUnits.map(
        (unit) =>
            run.entities.find((entity) => entity.entityType === unit.entityType && entity.entityId === unit.entityId)?.symbol ??
            `${unit.entityType} #${unit.entityId}`
    );
    return (
        <Callout title={hasExclusions ? 'Completed with exclusions' : 'Research coverage'} variant={hasExclusions ? 'warning' : 'info'}>
            <p>{describeEntryCoverage(coverage)}</p>
            {excludedSymbols.length > 0 && (
                <p className="mt-2">Whole entities excluded: {excludedSymbols.join(', ')}. See Diagnostics for their evidence.</p>
            )}
            {coverage.skippedSessionCount > 0 && (
                <>
                    <p className="mt-2 text-sm">
                        Missing input can affect later sessions that require its history. These counts describe excluded entry evaluations,
                        not missing source bars or missed trades. Every variant uses the same exclusions; unresolved outcomes after entry
                        are reported separately in the result cells.
                    </p>
                    <Disclosure title="Affected symbols and months" className="mt-3">
                        <div className="space-y-4">
                            <DataTable columns={entityColumns} data={coverage.byEntity} />
                            <DataTable columns={monthColumns} data={coverage.byMonth} search={false} />
                            <p className="text-xs">
                                These summaries cover all completed evaluations. Diagnostics contains the oldest{' '}
                                {coverage.returnedSessionCount} detailed exclusions{coverage.truncated ? ' (truncated)' : ''}.
                            </p>
                        </div>
                    </Disclosure>
                </>
            )}
        </Callout>
    );
};
export default RunCoverageSummary;
